
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { Session, User } from "@supabase/supabase-js";
import { supabase, refreshAuthStorage } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { logger } from "@/utils/logger";
import { setRememberMePreference, getRememberMePreference, clearAllAuthStorage } from "@/utils/authStorage";
import { isMobileDevice } from "@/utils/deviceUtils";
import { authService } from "@/services/authService";
import { Profile, UserMarket, AuthContextType } from "@/types/auth";
import { authRateLimiter, getSecureErrorMessage } from "@/utils/securityUtils";
import SecurityAuditService from "@/services/securityAuditService";
import { trackSession, getCurrentSessionId } from "@/services/sessionService";
import { twoFactorService } from "@/services/twoFactorService";
import { setSentryUser, clearSentryUser } from "@/lib/sentry";
import { safeStorage } from "@/utils/safariStorage";

// Create the context with a default value
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [markets, setMarkets] = useState<UserMarket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState(false);
  const [pending2FAUserId, setPending2FAUserId] = useState<string | null>(null);
  const [pending2FAMethod, setPending2FAMethod] = useState<'totp' | 'sms' | null>(null);
  const [tempSessionData, setTempSessionData] = useState<{ session: Session; user: User; profile: any } | null>(null);
  const navigate = useNavigate();

  // Initialize auth state
  useEffect(() => {
    const initStartTime = Date.now();
    logger.debug("AuthProvider: Initializing auth state", { timestamp: initStartTime });
    
    // Fallback timeout - ensures loading is set to false even if async operations hang
    const fallbackTimeoutId = setTimeout(() => {
      console.warn('⚠️ AuthContext: Fallback timeout triggered (8s) - forcing loading to false');
      setIsLoading(false);
    }, 8000);
    
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        const elapsed = Date.now() - initStartTime;
        logger.debug("Auth state change", { event, hasSession: !!session, elapsedMs: elapsed });
        setSession(session);
        setUser(session?.user ?? null);
        
        // Use setTimeout to avoid potential deadlock with Supabase client
        if (session?.user) {
          logger.debug("User authenticated, fetching profile data");
          setTimeout(async () => {
            try {
              await trackSession();
            } catch (error) {
              console.error('Error tracking session:', error);
            }
            
            // Only fetch seller profile if not a buyer
            if (session.user.user_metadata?.user_type !== 'buyer') {
              fetchProfile(session.user.id);
            } else {
              logger.debug("Buyer user detected in auth listener, skipping seller profile fetch");
              setProfile(null);
            }
            
            // Always fetch markets (shared by both user types)
            fetchMarkets(session.user.id);
          }, 0);
        } else {
          logger.debug("User not authenticated, clearing profile data");
          setProfile(null);
          setMarkets([]);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const elapsed = Date.now() - initStartTime;
      logger.debug("Initial session check", { hasSession: !!session, elapsedMs: elapsed });
      
      // Validate that session matches remember me preference
      const rememberMe = getRememberMePreference();
      
      if (session) {
        // If remember me is disabled, check if session is in wrong storage
        if (!rememberMe) {
          try {
            const sessionInLocalStorage = safeStorage.getItem('godyspo_auth_token') !== null;
            
            if (sessionInLocalStorage) {
              logger.warn('Found localStorage session with remember me disabled - clearing');
              await supabase.auth.signOut();
              clearAllAuthStorage();
              setSession(null);
              setUser(null);
              setIsLoading(false);
              clearTimeout(fallbackTimeoutId);
              return;
            }
          } catch (storageError) {
            // Storage access failed (Safari Private Mode) - continue with session
            console.warn('Storage check failed, continuing with session');
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        if (session?.user) {
          // Keep loading TRUE until profile fetch completes
          setTimeout(async () => {
            try {
              await trackSession();
            } catch (error) {
              console.error('Error tracking session:', error);
            }
            
            try {
              // Only fetch seller profile if not a buyer
              if (session.user.user_metadata?.user_type !== 'buyer') {
                await fetchProfile(session.user.id);
              } else {
                logger.debug("Buyer user detected in initial session, skipping seller profile fetch");
                setProfile(null);
              }
              
              await fetchMarkets(session.user.id);
            } finally {
              // Always set loading to false, even if profile fetch fails
              const totalElapsed = Date.now() - initStartTime;
              logger.debug("Auth initialization complete", { totalElapsedMs: totalElapsed });
              setIsLoading(false);
              clearTimeout(fallbackTimeoutId);
            }
          }, 0);
        } else {
          // Session exists but no user (edge case)
          setIsLoading(false);
          clearTimeout(fallbackTimeoutId);
        }
      } else {
        // No session - set loading to false immediately
        const totalElapsed = Date.now() - initStartTime;
        logger.debug("No session found, auth initialization complete", { totalElapsedMs: totalElapsed });
        setIsLoading(false);
        clearTimeout(fallbackTimeoutId);
      }
    }).catch((error) => {
      console.error('AuthContext: Error getting session:', error);
      setIsLoading(false);
      clearTimeout(fallbackTimeoutId);
    });

    return () => {
      subscription.unsubscribe();
      clearTimeout(fallbackTimeoutId);
    };
  }, []);

  // Fetch user profile
  const fetchProfile = async (userId: string) => {
    // Check if user is a buyer by checking metadata
    const { data: { user } } = await supabase.auth.getUser();
    
    // If user metadata indicates they're a buyer, skip seller profile fetch
    if (user?.user_metadata?.user_type === 'buyer') {
      logger.debug("User is a buyer, skipping seller profile fetch");
      setProfile(null);
      return;
    }
    
    // Only fetch seller profile if user is not explicitly a buyer
    const profileData = await authService.fetchProfile(userId);
    setProfile(profileData);
  };

  // Fetch user markets
  const fetchMarkets = async (userId: string) => {
    const marketsData = await authService.fetchMarkets(userId);
    setMarkets(marketsData);
  };

  // Refresh profile data
  const refreshProfile = async () => {
    if (user) {
      logger.debug("Refreshing profile data");
      await fetchProfile(user.id);
      await fetchMarkets(user.id);
    }
  };

  // Sign in with email and password (for sellers) - Fixed remember me timing
  const signIn = async (email: string, password: string, rememberMe: boolean = false) => {
    // Create timeout promise to prevent indefinite hanging (e.g., in Chrome Custom Tabs)
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error('Sign in is taking too long. Please try again.'));
      }, 30000); // 30 second timeout
    });

    const performSignIn = async () => {
      // Check rate limiting
      if (authRateLimiter.isRateLimited(email)) {
        throw new Error('Too many attempts. Please wait before trying again.');
      }

      // Record the attempt
      authRateLimiter.recordAttempt(email);

      console.log("Starting seller sign in for:", email);
      logger.debug("Starting sign in process", { isMobile: isMobileDevice(), rememberMe });
      
      // Set remember me preference BEFORE authentication (synchronously)
      setRememberMePreference(rememberMe, 'seller');
      logger.debug("Remember me preference set", { rememberMe });
      
      console.log("Attempting Supabase authentication...");
      const { data, error } = await supabase.auth.signInWithPassword({ 
        email, 
        password 
      });
      
      if (error) {
        console.error("Supabase auth error:", error);
        logger.error("Sign in error", { error: error.message });
        throw error;
      }
      
      // Refresh storage to ensure session is in correct storage type
      await refreshAuthStorage();
      logger.debug("Auth storage refreshed based on remember me preference");
      
      console.log("Supabase auth successful for user:", data.user?.id);
      logger.debug("Sign in successful, checking 2FA and profile");
      
      if (data.user) {
        // Check if user has 2FA enabled BEFORE completing sign-in
        const { data: user2FA, error: twoFAError } = await supabase
          .from('user_2fa')
          .select('enabled, method')
          .eq('user_id', data.user.id)
          .maybeSingle() as { data: { enabled: boolean; method: string } | null; error: any };

        if (!twoFAError && user2FA?.enabled) {
          // Check if current device is trusted
          const deviceId = getCurrentSessionId();
          const isTrusted = await twoFactorService.isDeviceTrusted(data.user.id, deviceId);

          if (isTrusted) {
            console.log("Device is trusted, skipping 2FA");
            logger.debug("Trusted device detected, bypassing 2FA");
            // Continue with normal sign-in flow (the code below will handle it)
          } else {
            // Device not trusted, require 2FA
            console.log("User has 2FA enabled, requiring verification");
            logger.debug("2FA enabled for user, deferring sign-in completion");
            
            const method = user2FA.method as 'totp' | 'sms';
            
            // Auto-trigger SMS for SMS users
            if (method === 'sms') {
              try {
                await supabase.functions.invoke('send-2fa-sms', {
                  body: { user_id: data.user.id }
                });
                console.log("SMS code sent automatically");
              } catch (smsError) {
                console.error("Failed to send SMS code:", smsError);
              }
            }
            
            // Don't complete sign-in yet - wait for 2FA verification
            setPending2FA(true);
            setPending2FAUserId(data.user.id);
            setPending2FAMethod(method);
            setTempSessionData({
              session: data.session,
              user: data.user,
              profile: null
            });
            return; // Exit early, don't navigate
          }
        }

        console.log("No 2FA or 2FA disabled, continuing with normal sign-in");
        
        // Check if user has a seller profile (profiles table)
        console.log("Checking for seller profile...");
        const { data: sellerProfile, error: profileError } = await supabase
          .from("profiles")
          .select("id, full_name, business_name")
          .eq("id", data.user.id)
          .maybeSingle();

        console.log("Seller profile query result:", { sellerProfile, profileError });

        if (profileError) {
          console.error("Error checking seller profile:", profileError);
          logger.error("Error checking seller profile", { error: profileError.message });
          
          // Sign out and show error
          await supabase.auth.signOut();
          toast({
            title: "Account Issue",
            description: "We couldn't verify your account. Please try signing in again or contact support.",
            variant: "destructive",
          });
          return;
        }

        // Check if this user is a buyer instead
        const { data: buyerCheck } = await supabase
          .from("buyer_profiles")
          .select("id")
          .eq("user_id", data.user.id)
          .maybeSingle();

        if (buyerCheck) {
          console.log("User is a buyer, not a seller");
          logger.warn("Buyer attempting to sign in as seller");
          await supabase.auth.signOut();
          toast({
            title: "Wrong login page",
            description: "This is a buyer account. Please use the buyer login page.",
            variant: "destructive",
          });
          return;
        }

        if (sellerProfile) {
          console.log("Seller profile found:", sellerProfile);
          logger.debug("Seller profile found, navigating to dashboard");
          
          // Reset rate limiting on successful login
          authRateLimiter.reset(email);
          
          // Set Sentry user context for error tracking
          setSentryUser({ id: data.user.id, email: data.user.email }, 'seller');
          
          // Update last login timestamp
          await supabase.rpc('update_last_login');
          
          // Log successful login
          await SecurityAuditService.logLoginSuccess({ user_type: 'seller' });
          
          // Navigate immediately without setTimeout
          navigate("/seller/dashboard", { replace: true });
          toast({
            title: "Welcome back!",
            description: `Signed in successfully as ${sellerProfile.full_name || sellerProfile.business_name || 'seller'}`,
          });
        } else {
          console.log("No seller profile found for user:", data.user.id);
          logger.warn("No seller profile found, signing out");
          // User exists but is not a seller, sign them out and show error
          await supabase.auth.signOut();
          toast({
            title: "Access denied",
            description: "This account is not registered as a seller. Please use the buyer login or create a seller account.",
            variant: "destructive",
          });
        }
      }
    };

    try {
      setIsLoading(true);
      
      // Race sign-in against timeout to prevent indefinite hanging
      await Promise.race([performSignIn(), timeoutPromise]);
    } catch (error: any) {
      console.error("Unexpected error during seller sign in:", error);
      logger.error("Sign in failed", { error: error.message });
      
      // On timeout, clear potentially corrupted auth state
      if (error.message && error.message.includes('taking too long')) {
        console.log('Sign in timeout - clearing auth storage');
        clearAllAuthStorage();
        await supabase.auth.signOut().catch(() => {});
      }
      
      // Log failed login attempt
      await SecurityAuditService.logLoginFailure(error.message, { user_type: 'seller' });
      
      // Use secure error messaging
      const secureMessage = getSecureErrorMessage(error);
      
      // Re-throw with secure message so LoginPage can handle it
      throw new Error(secureMessage);
    } finally {
      setIsLoading(false);
    }
  };

  // Complete 2FA verification after code entry
  const complete2FAVerification = async () => {
    try {
      logger.debug("Completing 2FA verification for seller");
      
      // Clear pending 2FA state
      setPending2FA(false);
      setPending2FAUserId(null);
      
      // Session should already be established at this point
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.user) {
        // Fetch profile and navigate
        await fetchProfile(session.user.id);
        await fetchMarkets(session.user.id);
        
        navigate("/seller/dashboard", { replace: true });
        toast({
          title: "Welcome back!",
          description: "Two-factor authentication verified successfully.",
        });
      }
    } catch (error: any) {
      logger.error("Error completing 2FA verification", { error: error.message });
      toast({
        title: "Error",
        description: "Failed to complete sign-in. Please try again.",
        variant: "destructive",
      });
    }
  };

  // Sign up with email and password
  const signUp = async (
    email: string, 
    password: string, 
    userData: Partial<Profile>,
    stateIds: string[]
  ) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await authService.signUp(email, password, userData, stateIds);
      
      if (error) {
        throw error;
      }

      if (data.user) {
        navigate("/seller/dashboard");
        toast({
          title: "Welcome to Go Dyspo!",
          description: "Your account has been created successfully.",
        });
      }
    } catch (error: any) {
      logger.error("Sign up failed", { error: error.message });
      toast({
        title: "Sign up failed",
        description: error.message || "An error occurred during sign up.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Sign out
  const signOut = async () => {
    try {
      logger.debug("Starting sign out process");
      setIsLoading(true);
      
      // Delete the current session from the database
      try {
        const { deleteCurrentSession } = await import("@/services/sessionService");
        await deleteCurrentSession();
        logger.debug("Session deleted from database");
      } catch (sessionError) {
        // Log but don't block logout if session deletion fails
        logger.error("Failed to delete session", { error: sessionError });
      }
      
      // Clear Sentry user context
      clearSentryUser();
      
      // Clear pending 2FA state
      setPending2FA(false);
      setPending2FAUserId(null);
      setTempSessionData(null);
      
      // Check if running in Lovable preview
      const isPreview = typeof window !== 'undefined' && 
                       new URLSearchParams(window.location.search).has('__lovable_token');
      
      // Get remember me preference before clearing
      const rememberMe = getRememberMePreference();
      
      // Clear local state first
      setSession(null);
      setUser(null);
      setProfile(null);
      setMarkets([]);
      
      // Perform Supabase logout
      await supabase.auth.signOut();
      
      // For Lovable preview OR when remember me was disabled, do aggressive cleanup
      if (isPreview || !rememberMe) {
        logger.debug("Performing deep cleanup (preview or remember me disabled)");
        clearAllAuthStorage();
        
        // Use direct navigation for preview to avoid parameter restoration
        if (isPreview) {
          toast({
            title: "Signed out",
            description: "You have been successfully signed out.",
          });
          
          // Force reload to clean URL without __lovable_token
          window.location.href = window.location.origin + '/seller/login';
          return;
        }
      }
      
      // Normal navigation for production
      navigate("/seller/login", { replace: true });
      toast({
        title: "Signed out",
        description: "You have been successfully signed out.",
      });
    } catch (error: any) {
      logger.error("Sign out failed", { error: error.message });
      
      // Force cleanup even on error
      clearAllAuthStorage();
      
      toast({
        title: "Sign out failed",
        description: error.message || "An error occurred during sign out.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update user profile
  const updateProfile = async (updates: Partial<Profile>) => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      await authService.updateProfile(user.id, updates);
      await fetchProfile(user.id);
      
      toast({
        title: "Profile updated",
        description: "Your profile has been updated successfully.",
      });
    } catch (error: any) {
      logger.error("Profile update failed", { error: error.message });
      toast({
        title: "Profile update failed",
        description: error.message || "An error occurred while updating your profile.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Update user markets
  const updateMarkets = async (stateIds: string[]) => {
    if (!user) return;
    
    try {
      setIsLoading(true);
      
      await authService.updateMarkets(user.id, stateIds);
      await fetchMarkets(user.id);
      
      toast({
        title: "Markets updated",
        description: "Your markets have been updated successfully.",
      });
    } catch (error: any) {
      logger.error("Markets update failed", { error: error.message });
      toast({
        title: "Markets update failed",
        description: error.message || "An error occurred while updating your markets.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        profile,
        markets,
        isLoading,
        pending2FA,
        pending2FAUserId,
        pending2FAMethod,
        complete2FAVerification,
        signIn,
        signUp,
        signOut,
        updateProfile,
        updateMarkets,
        refreshProfile
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use the auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
